import React, { useState, useEffect } from 'react';
import { paymentApi } from '../../utils/request';
import './styles.css';
import { UserInfo } from '../../utils/types';
import { useNavigate } from 'react-router-dom';


interface VipLevel {
  id: number;
  name: string;
  price: number;
  benefits: string[];
  icon: string;
}

interface PurchaseModalProps {
  visible: boolean;
  level: VipLevel | null;
  onClose: () => void;
  onConfirm: () => void;
}

interface PaymentModalProps {
  visible: boolean;
  level: VipLevel | null;
  onClose: () => void;
  onPaymentSuccess: () => void;
  currentUser: UserInfo;
}

interface ResultModalProps {
  visible: boolean;
  type: 'success' | 'error';
  message: string;
  onClose: () => void;
}

interface BindSuperiorModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (inviteCode: string) => void;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({ visible, level, onClose, onConfirm }) => {
  if (!visible || !level) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content purchase-modal">
        <div className="modal-header">
          <h3>确认购买</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="purchase-info">
            <img style={{width: '50%', height: '50%'}} src={level.icon} alt="会员等级" />
            <h4>{level.name}</h4>
            <div className="price-tag">¥ {level.price}</div>
          </div>
          <div className="benefits-list">
            <h5>会员权益</h5>
            <ul>
              {level.benefits.map((benefit, index) => (
                <li key={index}>{benefit}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>取消</button>
          <button className="confirm-btn" onClick={onConfirm}>确认购买</button>
        </div>
      </div>
    </div>
  );
};

const ResultModal: React.FC<ResultModalProps> = ({ visible, type, message, onClose }) => {
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div className="modal-overlay result-modal-overlay">
      <div className={`result-modal ${type}`}>
        <div className="result-icon">
          {type === 'success' ? '✅' : '❌'}
        </div>
        <div className="result-message">{message}</div>
      </div>
    </div>
  );
};

const PaymentModal: React.FC<PaymentModalProps> = ({ visible, level, onClose, onPaymentSuccess, currentUser }) => {
  const [selectedPayment, setSelectedPayment] = useState<string>('balance');
  const [isLoading, setIsLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [paymentResult, setPaymentResult] = useState<{type: 'success' | 'error', message: string}>({
    type: 'success',
    message: ''
  });
  const [orderId, setOrderId] = useState<string>('');

  
  const paymentMethods = [
    { id: 'balance', name: '余额支付', icon: '/icons/wallet.png' },
    // { id: 'wechat', name: '微信支付', icon: '💚' },
    { id: 'points', name: '积分支付', icon: '/icons/point.png' }
  ];

  // 轮询支付状态
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (orderId && isLoading) {
      timer = setInterval(async () => {
        try {
          const result = await paymentApi.queryOrderStatus(orderId);
          if (result.status === 'success') {
            setPaymentResult({
              type: 'success',
              message: '支付成功！'
            });
            setShowResult(true);
            setIsLoading(false);
            clearInterval(timer);
            setTimeout(() => {
              onPaymentSuccess();
            }, 2000);
          } else if (result.status === 'failed') {
            throw new Error(result.message || '支付失败');
          }
        } catch (error) {
          setPaymentResult({
            type: 'error',
            message: error instanceof Error ? error.message : '支付失败，请重试'
          });
          setShowResult(true);
          setIsLoading(false);
          clearInterval(timer);
        }
      }, 2000); // 每2秒查询一次
    }
    
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [orderId, isLoading, onPaymentSuccess]);

  const handlePayment = async () => {
    if (!level) return;


    if(selectedPayment === 'balance'){
      if(currentUser.balance < level.price){
        setPaymentResult({
          type: 'error',
          message: '余额不足'
        });
        setShowResult(true);
        return;
      }
    }

    if(selectedPayment === 'points'){
      if(currentUser.points < level.price){
        setPaymentResult({
          type: 'error',
          message: '积分不足'
        });
        setShowResult(true);
        return;
      }
    }

    setIsLoading(true);
    try {
      // 创建支付订单
      const orderResult = await paymentApi.createOrder({
        levelId: level.id,
        paymentMethod: selectedPayment,
        amount: level.price
      });
      
      setOrderId(orderResult.orderId);
      
      // 如果是微信或支付宝支付，需要跳转到支付页面
      if (selectedPayment !== 'balance' && selectedPayment !== 'points' && orderResult.paymentUrl) {
        window.location.href = orderResult.paymentUrl;
        return;
      }
      
      // 余额支付直接轮询状态
      // 轮询逻辑在useEffect中处理
      
    } catch (error) {
      setPaymentResult({
        type: 'error',
        message: error instanceof Error ? error.message : '创建订单失败，请重试'
      });
      setShowResult(true);
      setIsLoading(false);
    }
  };

  // 取消支付
  const handleCancel = async () => {
    if (orderId && isLoading) {
      try {
        await paymentApi.cancelOrder(orderId);
      } catch (error) {
        console.error('取消支付失败:', error);
      }
    }
    setIsLoading(false);
    onClose();
  };

  if (!visible || !level) return null;

  return (
    <>
      <div className="modal-overlay">
        <div className="modal-content payment-modal">
          <div className="modal-header">
            <h3>选择支付方式</h3>
            <button className="close-btn" onClick={onClose} disabled={isLoading}>×</button>
          </div>
          <div className="modal-body">
            <div className="payment-amount">
              <span className="amount-label">支付金额</span>
              <span className="amount-value">¥ {level.price}</span>
            </div>
            <div className="payment-methods">
              {paymentMethods.map(method => (
                <div
                  key={method.id}
                  className={`payment-method-item ${selectedPayment === method.id ? 'selected' : ''}`}
                  onClick={() => !isLoading && setSelectedPayment(method.id)}
                >
                  <span className="payment-icon"><img style={{width: '32px', height: '32px'}} src={method.icon} alt="支付方式" /></span>
                  <span className="payment-name">{method.name}</span>
                  <span className="payment-check">✓</span>
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button 
              className="cancel-btn" 
              onClick={handleCancel}
              disabled={isLoading}
            >
              取消
            </button>
            <button 
              className={`pay-btn ${isLoading ? 'loading' : ''}`}
              onClick={handlePayment}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loading-text">
                  <span className="loading-spinner"></span>
                  支付中...
                </span>
              ) : (
                `立即支付 ¥${level.price}`
              )}
            </button>
          </div>
        </div>
      </div>
      <ResultModal
        visible={showResult}
        type={paymentResult.type}
        message={paymentResult.message}
        onClose={() => setShowResult(false)}
      />
    </>
  );
};

const BindSuperiorModal: React.FC<BindSuperiorModalProps> = ({ visible, onClose, onConfirm }) => {
  const [inviteCode, setInviteCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!inviteCode.trim()) {
      alert('请输入邀请码');
      return;
    }
    
    setIsLoading(true);
    try {
      await onConfirm(inviteCode.trim());
      setInviteCode('');
    } catch (error) {
      console.error('绑定失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setInviteCode('');
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content bind-superior-modal">
        <div className="modal-header">
          <h3>绑定上级</h3>
          <button className="close-btn" onClick={handleClose} disabled={isLoading}>×</button>
        </div>
        <div className="modal-body">
          <div className="bind-form">
            <div className="form-group">
              <label htmlFor="inviteCode">上级邀请码</label>
              <input
                id="inviteCode"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="请输入上级邀请码"
                disabled={isLoading}
                maxLength={20}
              />
            </div>
            <div className="bind-tip">
              <span className="tip-icon">💡</span>
              <span>请输入您的上级邀请码，绑定后即可享受团队收益</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button 
            className="cancel-btn" 
            onClick={handleClose}
            disabled={isLoading}
          >
            取消
          </button>
          <button 
            className={`confirm-btn ${isLoading ? 'loading' : ''}`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading-text">
                <span className="loading-spinner"></span>
                绑定中...
              </span>
            ) : (
              '确认绑定'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const VipPurchase: React.FC = () => {
  const navigate = useNavigate(); // 确保这行在组件顶部
  
  const [currentUser, setCurrentUser] = useState<UserInfo>({
    nickname: '用户昵称',
    currentLevel: 0,
    avatar: '/icons/avatar.png',
    balance: 0.00,
    points: 0,
    token: ''
  });
  
  useEffect(() => {
    // 从URL获取token参数
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const nicknameParam = urlParams.get('nickname');
    const avatarParam = urlParams.get('avatar');
    const balanceParam = urlParams.get('balance');
    const pointsParam = urlParams.get('points');
    if (tokenParam) {
      setCurrentUser({
        ...currentUser,
        token: tokenParam,
        nickname: nicknameParam || '用户昵称',
        avatar: avatarParam || '/icons/avatar.png',
        balance: balanceParam ? parseFloat(balanceParam) : 0,
        points: pointsParam ? parseInt(pointsParam) : 0
      });
    } else {
      console.warn('未找到token参数');
    }
  }, []);

  

  const vipLevels: VipLevel[] = [
    {
      id: 1,
      name: 'VIP1',
      price: 888,
      icon: '/icons/vip1.png',
      benefits: ['赠送888积分']
    },
    {
      id: 2,
      name: 'VIP2',
      price: 2888,
      icon: '/icons/vip2.png',
      benefits: ['赠送2888积分']
    },
    {
      id: 3,
      name: 'VIP3',
      price: 8888,
      icon: '/icons/vip3.png',
      benefits: ['赠送8888积分']
    },
    {
      id: 4,
      name: 'VIP4',
      price: 38888,
      icon: '/icons/vip4.png',
      benefits: [ '赠送38888积分']
    },
    {
      id: 5,
      name: 'VIP5',
      price: 88888,
      icon: '/icons/vip5.png',
      benefits: ['赠送88888积分']
    }
  ];

  const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
  const [purchaseModalVisible, setPurchaseModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [currentPurchaseLevel, setCurrentPurchaseLevel] = useState<VipLevel | null>(null);
  const [bindSuperiorModalVisible, setBindSuperiorModalVisible] = useState(false);

  const handlePurchase = (level: VipLevel) => {
    setCurrentPurchaseLevel(level);
    setPurchaseModalVisible(true);
  };

  const handlePurchaseConfirm = () => {
    setPurchaseModalVisible(false);
    setPaymentModalVisible(true);
  };

  const handlePaymentSuccess = () => {
    setPaymentModalVisible(false);
    // 在支付成功时将token传递给后端
    console.log('支付成功，当前token:', currentUser.token);
    // TODO: 调用后端API时带上token
    alert('支付成功！');
  };

  // 处理我的团队按钮点击
  const handleTeamClick = () => {
    console.log('点击我的团队按钮');
    
    if (!currentUser.token) {
      alert('请先登录');
      return;
    }
    
    // 直接使用 window.location.href
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const nicknameParam = urlParams.get('nickname');
    const avatarParam = urlParams.get('avatar');
    const balanceParam = urlParams.get('balance');
    
    const teamUrl = `/team?token=${tokenParam}&nickname=${nicknameParam}&avatar=${avatarParam}&balance=${balanceParam}`;
    console.log('跳转到:', teamUrl);
    
    window.location.href = teamUrl;
  };

  // 处理分红明细按钮点击
  const handleDividendClick = () => {
    console.log('点击分红明细，当前token:', currentUser.token);
    
    if (!currentUser.token) {
      alert('请先登录');
      return;
    }
    
    // 使用 React Router 的 navigate 进行页面跳转
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    const nicknameParam = urlParams.get('nickname');
    const avatarParam = urlParams.get('avatar');
    const balanceParam = urlParams.get('balance');
    
    const dividendUrl = `/dividend?token=${tokenParam}&nickname=${nicknameParam}&avatar=${avatarParam}&balance=${balanceParam}`;
    navigate(dividendUrl);
  };

  // 处理绑定上级按钮点击
  const handleBindSuperiorClick = () => {
    setBindSuperiorModalVisible(true);
  };

  // 处理绑定上级确认
  const handleBindSuperiorConfirm = async (inviteCode: string) => {
    try {
      // TODO: 调用后端API绑定上级
      console.log('绑定上级，邀请码:', inviteCode, '当前token:', currentUser.token);
      
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      alert('绑定成功！');
      setBindSuperiorModalVisible(false);
    } catch (error) {
      alert('绑定失败，请重试');
      throw error;
    }
  };

  // 渲染个人信息区域
  const renderUserInfo = () => {
    if (!currentUser.token) {
      return (
        <div className="user-info not-logged-in">
          <div className="user-basic">
            <div className="avatar">
              <img src="/icons/avatar.png" alt="访客头像" />
            </div>
            <div className="user-details">
              <h2>未登录</h2>
              <div className="login-tip">请先登录后购买会员</div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="user-info">
        <div className="user-basic">
          <div className="avatar">
            <img src={currentUser.avatar} alt="用户头像" />
          </div>
          <div className="user-details">
            <h2>{currentUser.nickname}</h2>
            {currentUser.currentLevel > 0 ? (
              <img style={{width: '30%', height: '32px',marginLeft: '-10px'}} src={vipLevels[currentUser.currentLevel - 1].icon} alt="会员等级" />
            ) : (
              <span className="level-badge">普通会员</span>
            )}
          </div>
        </div>
        <div className="user-stats">
          <div className="stat-item">
            <span className="stat-value">¥{currentUser.balance.toFixed(2)}</span>
            <span className="stat-label"><img style={{width: '16px', height: '16px', marginRight: '4px'}} src="/icons/wallet.png" alt="钱包余额" />钱包余额</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{currentUser.points}</span>
            <span className="stat-label"><img style={{width: '16px', height: '16px', marginRight: '4px'}} src="/icons/point.png" alt="AIQI积分" />AIQI积分</span>
          </div>
        </div>
        <div className="user-actions">
          <button className="action-btn team-btn" onClick={handleTeamClick}>
            <span className="action-icon">👥</span>
            我的团队
          </button>
          <button className="action-btn dividend-btn" onClick={() => handleDividendClick()}>
            <span className="action-icon">💰</span>
            分红明细
          </button>
        </div>
        <button className="bind-superior-btn" onClick={() => handleBindSuperiorClick()}>
          <span className="bind-icon">🔗</span>
          绑定上级
        </button>
      </div>
    );
  };

  return (
    <div className="vip-purchase-container">
      <div className="bg-decoration"></div>
      <div className="bg-decoration"></div>
      
      {renderUserInfo()}

      <div className="vip-levels">
        <h3>选择会员等级</h3>
        <div className="level-cards">
          {vipLevels.map((level) => (
            <div 
              key={level.id} 
              className={`level-card ${selectedLevel === level.id ? 'selected' : ''} ${!currentUser.token ? 'disabled' : ''}`}
              onClick={() => currentUser.token && setSelectedLevel(level.id)}
            >
              <div className="level-card-left">
                <img style={{width: '100%', height: '100%'}} src={level.icon} alt="会员等级" />
                {/* <div className="level-name">{level.name}</div> */}
                <div className="price">{level.price}</div>
              </div>
              <div className="level-card-right">
                <ul className="benefits">
                  {level.benefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>
              <button 
                className="purchase-button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (currentUser.token) {
                    handlePurchase(level);
                  }
                  
                }}
              >
                {currentUser.token ? '升级VIP' : '请先登录'}
              </button>
            </div>
          ))}
        </div>
      </div>

      <PurchaseModal
        visible={purchaseModalVisible}
        level={currentPurchaseLevel}
        onClose={() => setPurchaseModalVisible(false)}
        onConfirm={handlePurchaseConfirm}
      />

      <PaymentModal
        visible={paymentModalVisible}
        level={currentPurchaseLevel}
        onClose={() => setPaymentModalVisible(false)}
        onPaymentSuccess={handlePaymentSuccess}
        currentUser={currentUser}
      />

      <BindSuperiorModal
        visible={bindSuperiorModalVisible}
        onClose={() => setBindSuperiorModalVisible(false)}
        onConfirm={handleBindSuperiorConfirm}
      />
    </div>
  );
};

export default VipPurchase; 